// Live client behavior for the active `platform.app.config` dashboard.
// Treat this alongside the page modules as authoritative implementation truth; do not infer
// current behavior from tests, layout JSON, or generated artisan reports before checking the
// live page/client modules.
module.exports = async function(context) {
  var dashboardApi = (context || {}).dashboardApi;
  var dashboard = (context || {}).dashboard || {};
  var currentPage = dashboard.currentPage || {};
  var pageNamespace = (currentPage.namespace || "").toString().trim();
  var apiUrl = (((currentPage || {}).routes || {}).api || "").toString().trim();
  var supportsResetBehavior = pageNamespace === "platform.app.config";
  var supportsRawJsonPreview = pageNamespace === "platform.app.config.json";
  if (!supportsResetBehavior && !supportsRawJsonPreview) return;


  function getSelectedEnvironment() {
    var explicit = (currentPage.envName || currentPage.environment || currentPage.env || "").toString().trim();
    if (explicit) return explicit;
    try {
      var params = new URLSearchParams((window.location && window.location.search) || "");
      return (params.get("env") || "").toString().trim();
    } catch (error) {
      return "";
    }
  }


  function getEditorRoot(button) {
    var wizard = button.closest("[data-wizard='true']");
    if (!wizard) return null;
    return wizard.querySelector("[data-config-editor-root='true']") || wizard;
  }

  function getResultHost(button) {
    if (!button) return null;
    var wrapper = button.parentElement;
    if (!wrapper) return null;
    return wrapper.querySelector("[data-config-reset-result='true']");
  }

  function setResult(button, tone, message) {
    var host = getResultHost(button);
    if (!host) return;
    if (!message) {
      host.className = "platform-config-reset-result mt-2 small d-none";
      host.innerHTML = "";
      return;
    }
    host.className = "platform-config-reset-result mt-2 small alert alert-" + tone + " mb-0 py-2 px-3";
    host.textContent = message;
  }

  function hasOverride(button) {
    return (button.getAttribute("data-config-has-override") || "").toString().trim().toLowerCase() === "true";
  }

  function setHasOverride(button, value) {
    if (!button) return;
    button.setAttribute("data-config-has-override", value ? "true" : "false");
  }

  async function syncButton(button) {
    if (!button) return;
    button.classList.toggle("d-none", !hasOverride(button));
  }

  var DANGEROUS_OBJECT_KEYS = {
    "__proto__": true,
    "prototype": true,
    "constructor": true
  };

  function isPlainObject(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
  }

  function isDangerousObjectKey(key) {
    return !!DANGEROUS_OBJECT_KEYS[(key || "").toString()];
  }

  function sanitizeConfigValue(value) {
    if (Array.isArray(value)) {
      return value.map(function(item) {
        return sanitizeConfigValue(item);
      });
    }

    if (!isPlainObject(value)) return value;

    var output = {};
    Object.keys(value).forEach(function(key) {
      if (isDangerousObjectKey(key)) return;
      output[key] = sanitizeConfigValue(value[key]);
    });
    return output;
  }

  function parseRawEnvironmentJson(text) {
    var parsed = {};
    try {
      parsed = text && text.toString().trim() ? JSON.parse(text) : {};
    } catch (error) {
      throw {
        namespace: "platform.app.config.raw.invalid"
      };
    }

    if (!isPlainObject(parsed)) {
      throw {
        namespace: "platform.app.config.raw.object.required"
      };
    }

    return sanitizeConfigValue(parsed);
  }

  function collectLeafPaths(value, prefix, target) {
    var output = target || {};
    var base = (prefix || "").toString().trim();

    if (Array.isArray(value)) {
      if (base) output[base] = JSON.stringify(value);
      return output;
    }

    if (!isPlainObject(value)) {
      if (base) output[base] = JSON.stringify(value);
      return output;
    }

    var keys = Object.keys(value);
    if (!keys.length && base) output[base] = "{}";

    keys.forEach(function(key) {
      var nextPath = base ? (base + "." + key) : key;
      collectLeafPaths(value[key], nextPath, output);
    });

    return output;
  }

  function buildReplacementPreview(currentDoc, nextDoc) {
    var currentPaths = collectLeafPaths(sanitizeConfigValue(currentDoc || {}));
    var nextPaths = collectLeafPaths(sanitizeConfigValue(nextDoc || {}));
    var allPaths = Object.keys(currentPaths).concat(Object.keys(nextPaths)).filter(function(pathValue, index, list) {
      return list.indexOf(pathValue) === index;
    }).sort();
    var added = [];
    var removed = [];
    var changed = [];

    allPaths.forEach(function(pathValue) {
      var hasCurrent = Object.prototype.hasOwnProperty.call(currentPaths, pathValue);
      var hasNext = Object.prototype.hasOwnProperty.call(nextPaths, pathValue);
      if (!hasCurrent && hasNext) return added.push(pathValue);
      if (hasCurrent && !hasNext) return removed.push(pathValue);
      if (currentPaths[pathValue] !== nextPaths[pathValue]) changed.push(pathValue);
    });

    return {
      added: added,
      removed: removed,
      changed: changed,
      totalImpactCount: added.length + removed.length + changed.length,
      samplePaths: added.concat(changed).concat(removed).slice(0, 12)
    };
  }

  function buildEffectiveConfigFromDraft(defaultDoc, nextDoc) {
    var merged = sanitizeConfigValue(defaultDoc || {});
    var source = sanitizeConfigValue(nextDoc || {});

    function mergeInto(target, addition) {
      var output = isPlainObject(target) ? sanitizeConfigValue(target) : {};
      if (!isPlainObject(addition)) return output;
      Object.keys(addition).forEach(function(key) {
        if (isDangerousObjectKey(key)) return;
        if (isPlainObject(addition[key])) {
          output[key] = mergeInto(output[key], addition[key]);
          return;
        }
        output[key] = sanitizeConfigValue(addition[key]);
      });
      return output;
    }

    return mergeInto(merged, source);
  }

  function buildRuntimeEffectPreview(runtimeDoc, defaultDoc, nextDoc, selectedEnv, activeEnv) {
    var runtimePaths = collectLeafPaths(sanitizeConfigValue(runtimeDoc || {}));
    var nextPaths = collectLeafPaths(buildEffectiveConfigFromDraft(defaultDoc, nextDoc));
    var allPaths = Object.keys(runtimePaths).concat(Object.keys(nextPaths)).filter(function(pathValue, index, list) {
      return list.indexOf(pathValue) === index;
    }).sort();
    var differsFromRuntime = [];
    var matchesRuntime = [];

    allPaths.forEach(function(pathValue) {
      var hasRuntime = Object.prototype.hasOwnProperty.call(runtimePaths, pathValue);
      var hasNext = Object.prototype.hasOwnProperty.call(nextPaths, pathValue);
      if (hasRuntime && hasNext && runtimePaths[pathValue] === nextPaths[pathValue]) {
        matchesRuntime.push(pathValue);
        return;
      }
      differsFromRuntime.push(pathValue);
    });

    return {
      selectedEnv: selectedEnv || 'selected',
      activeEnv: activeEnv || selectedEnv || 'selected',
      selectedIsActive: (selectedEnv || 'selected') === (activeEnv || selectedEnv || 'selected'),
      differsFromRuntime: differsFromRuntime,
      matchesRuntime: matchesRuntime,
      differsFromRuntimeCount: differsFromRuntime.length,
      matchesRuntimeCount: matchesRuntime.length,
      sampleRuntimeDiffPaths: differsFromRuntime.slice(0, 12)
    };
  }

  function replaceChildren(host, children) {
    if (!host) return;
    host.innerHTML = "";
    if (typeof host.replaceChildren === "function") {
      host.replaceChildren.apply(host, children);
      return;
    }
    while (host.firstChild) host.removeChild(host.firstChild);
    children.forEach(function(child) {
      host.appendChild(child);
    });
  }

  function createTextNode(tagName, className, text) {
    var node = document.createElement(tagName);
    if (className) node.className = className;
    node.textContent = text;
    return node;
  }

  function renderRawJsonPreview(host, preview, runtimePreview, envName) {
    if (!host) return;
    var environment = envName || "selected";
    var activeEnv = (runtimePreview && runtimePreview.activeEnv) || environment;
    var message = preview.totalImpactCount > 0
      ? ("This draft would change " + preview.totalImpactCount + " config paths in env." + environment + ".json.")
      : ("This draft currently matches env." + environment + ".json, so replacing the file would not change any saved config paths yet.");
    var runtimeMessage = runtimePreview && runtimePreview.selectedIsActive
      ? (runtimePreview.differsFromRuntimeCount > 0
        ? ("This selected environment is active now. The draft would leave " + runtimePreview.matchesRuntimeCount + " runtime-visible paths aligned and make " + runtimePreview.differsFromRuntimeCount + " paths differ from the app's current runtime snapshot until restart or preflight applies it.")
        : "This selected environment is active now. The draft currently matches the app's runtime snapshot for every runtime-visible path shown here.")
      : (runtimePreview && runtimePreview.differsFromRuntimeCount > 0
        ? ("This selected environment is not active now. The draft would stage " + runtimePreview.differsFromRuntimeCount + " paths that still differ from the app's current runtime on " + activeEnv + ".")
        : ("This selected environment is not active now. The draft matches the app's current runtime for every runtime-visible path shown here, but runtime still stays on " + activeEnv + " until that environment becomes active."));
    var cardBody = document.createElement("div");
    cardBody.className = "card-body";
    cardBody.appendChild(createTextNode("div", "fw-semibold mb-1", "Current replacement impact"));
    cardBody.appendChild(createTextNode("div", "small text-muted mb-2", message));

    var counts = document.createElement("div");
    counts.className = "small mb-2";
    counts.appendChild(createTextNode("span", "me-3", "Added: " + preview.added.length));
    counts.appendChild(createTextNode("span", "me-3", "Changed: " + preview.changed.length));
    counts.appendChild(createTextNode("span", "", "Removed: " + preview.removed.length));
    cardBody.appendChild(counts);

    cardBody.appendChild(createTextNode(
      "div",
      "small text-muted mb-3",
      preview.samplePaths.length ? ("Affected paths: " + preview.samplePaths.join(", ")) : "Affected paths will appear here as you edit the JSON above."
    ));
    cardBody.appendChild(createTextNode("div", "fw-semibold mb-1", "Runtime effect truth"));
    cardBody.appendChild(createTextNode("div", "small text-muted mb-2", runtimeMessage));

    var runtimeCounts = document.createElement("div");
    runtimeCounts.className = "small mb-2";
    runtimeCounts.appendChild(createTextNode("span", "me-3", "Differs from runtime: " + ((runtimePreview && runtimePreview.differsFromRuntimeCount) || 0)));
    runtimeCounts.appendChild(createTextNode("span", "", "Matches runtime: " + ((runtimePreview && runtimePreview.matchesRuntimeCount) || 0)));
    cardBody.appendChild(runtimeCounts);

    cardBody.appendChild(createTextNode(
      "div",
      "small text-muted",
      runtimePreview && runtimePreview.sampleRuntimeDiffPaths && runtimePreview.sampleRuntimeDiffPaths.length
        ? ("Paths that would still differ from runtime: " + runtimePreview.sampleRuntimeDiffPaths.join(", "))
        : "Paths that would still differ from runtime will appear here when the draft diverges from the active runtime snapshot."
    ));

    replaceChildren(host, [cardBody]);
  }

  function renderBlockedRawJsonPreview(host) {
    if (!host) return;
    replaceChildren(host, [
      (function() {
        var cardBody = document.createElement("div");
        cardBody.className = "card-body";
        cardBody.appendChild(createTextNode("div", "fw-semibold mb-1", "Current replacement impact"));
        cardBody.appendChild(createTextNode("div", "small mb-2", "This draft cannot be saved yet."));
        cardBody.appendChild(createTextNode("div", "small text-muted", "Save unavailable until the JSON above parses successfully and the top-level value is an object, so impact details stay blocked until that is corrected."));
        return cardBody;
      })()
    ]);
  }

  function parseRestorePreviewMap(rawValue) {
    try {
      return JSON.parse(decodeURIComponent((rawValue || "").toString())) || {};
    } catch (error) {
      return {};
    }
  }

  function renderRestorePreview(host, preview) {
    if (!host) return;
    var cardBody = document.createElement("div");
    cardBody.className = "card-body";
    cardBody.appendChild(createTextNode("div", "fw-semibold mb-1", "Selected backup recap"));
    if (preview.label) cardBody.appendChild(createTextNode("div", "small mb-1", preview.label));
    if (preview.meta) cardBody.appendChild(createTextNode("div", "small text-muted mb-2", preview.meta));
    cardBody.appendChild(createTextNode(
      "div",
      preview.summary ? "small mb-1" : "small text-muted mb-1",
      preview.summary || "Choose a backup above to review exactly how many config paths would be replaced."
    ));
    cardBody.appendChild(createTextNode(
      "div",
      "small text-muted",
      preview.runtimeSummary || "Choose a backup above to review whether the restored result would still differ from the active runtime."
    ));
    replaceChildren(host, [cardBody]);
  }

  function initRestorePreview() {
    var previewHost = document.querySelector("[data-restore-preview-host='true']");
    if (!previewHost) return;
    var wizard = previewHost.closest ? previewHost.closest("[data-wizard='true']") : null;
    if (!wizard) return;
    var backupField = wizard.querySelector("[name='backupFile']");
    if (!backupField) return;
    var restorePreviewMap = parseRestorePreviewMap(previewHost.getAttribute("data-restore-preview-map"));

    function refresh() {
      var selected = (backupField.value || "").toString();
      renderRestorePreview(previewHost, restorePreviewMap[selected] || {});
    }

    ["input", "change", "blur"].forEach(function(eventName) {
      backupField.addEventListener(eventName, refresh, true);
    });
    refresh();
  }

  function setRawJsonBlockedState(wizard, blocked) {
    if (!wizard) return;
    wizard.setAttribute("data-raw-json-save-blocked", blocked ? "true" : "false");

    var continueButton = wizard.querySelector(".wizard-footer button[id$='-continue']");
    if (continueButton) {
      continueButton.disabled = !!blocked;
      continueButton.setAttribute("aria-disabled", blocked ? "true" : "false");
      continueButton.title = blocked ? "Save unavailable until the JSON parses and the top-level value is an object." : "";
    }

    var instruction = wizard.querySelector("[data-raw-json-confirm-instructions]");
    if (instruction) {
      var readyCopy = (instruction.getAttribute("data-raw-json-ready-copy") || "").toString();
      var blockedCopy = (instruction.getAttribute("data-raw-json-blocked-copy") || "").toString();
      instruction.textContent = blocked ? blockedCopy : readyCopy;
      instruction.className = blocked ? "small text-danger mb-3" : "small text-muted mb-3";
      instruction.setAttribute("data-raw-json-confirm-instructions", blocked ? "blocked" : "ready");
    }

    var confirmInput = wizard.querySelector("[data-raw-json-confirm-input='true']") || wizard.querySelector("[name='confirmRawSave']");
    if (confirmInput) {
      confirmInput.disabled = !!blocked;
      confirmInput.setAttribute("aria-disabled", blocked ? "true" : "false");
    }
  }

  function initRawJsonPreview() {
    var previewRoot = document.querySelector("[data-raw-json-preview-root='true']");
    var previewHost = document.querySelector("[data-raw-json-preview-host='true']");
    if (!previewRoot || !previewHost) return;

    var sourceField = previewRoot.querySelector("textarea, [name='source']");
    if (!sourceField) return;

    var wizard = previewRoot.closest ? previewRoot.closest("[data-wizard='true']") : null;
    var currentRaw = decodeURIComponent((previewRoot.getAttribute("data-current-json") || "").toString());
    var runtimeRaw = decodeURIComponent((previewHost.getAttribute("data-runtime-json") || "").toString());
    var defaultRaw = decodeURIComponent((previewHost.getAttribute("data-default-json") || "").toString());
    var envName = (previewHost.getAttribute("data-selected-env") || getSelectedEnvironment() || "").toString().trim();
    var activeEnv = (previewHost.getAttribute("data-active-env") || "").toString().trim();

    function refresh() {
      try {
        var nextDoc = parseRawEnvironmentJson(sourceField.value || sourceField.textContent || "");
        var preview = buildReplacementPreview(
          parseRawEnvironmentJson(currentRaw),
          nextDoc
        );
        var runtimePreview = buildRuntimeEffectPreview(
          parseRawEnvironmentJson(runtimeRaw || "{}"),
          parseRawEnvironmentJson(defaultRaw || "{}"),
          nextDoc,
          envName,
          activeEnv
        );
        previewHost.className = "card bg-light border-0 mb-3";
        renderRawJsonPreview(previewHost, preview, runtimePreview, envName);
        setRawJsonBlockedState(wizard, false);
      } catch (error) {
        previewHost.className = "card border-warning bg-warning-subtle mb-3";
        renderBlockedRawJsonPreview(previewHost);
        setRawJsonBlockedState(wizard, true);
      }
    }

    ["input", "change", "blur"].forEach(function(eventName) {
      sourceField.addEventListener(eventName, refresh, true);
    });
    refresh();
  }

  if (supportsRawJsonPreview) initRawJsonPreview();
  if (supportsResetBehavior) initRestorePreview();
  if (!supportsResetBehavior) return;

  var resetButtons = document.querySelectorAll("button[data-config-reset-button='true']");
  resetButtons.forEach(function(button) {
    syncButton(button);

    var editorRoot = getEditorRoot(button);
    if (editorRoot) {
      ["input", "change", "blur"].forEach(function(eventName) {
        editorRoot.addEventListener(eventName, function() {
          syncButton(button);
          setResult(button, "", "");
        }, true);
      });
    }

    button.addEventListener("click", async function(event) {
      event.preventDefault();
      var namespace = (button.getAttribute("data-config-namespace") || "").toString().trim();
      if (!namespace) return;
      button.disabled = true;
      setResult(button, "", "");
      try {
        var selectedEnvironment = getSelectedEnvironment();
        var payload = { namespace: namespace };
        if (selectedEnvironment) payload.environment = selectedEnvironment;
        var response = apiUrl
          ? await dashboardApi.withBase(apiUrl).action("reset", payload)
          : await dashboardApi.action(pageNamespace, "reset", payload);

        setHasOverride(button, !!(response && response.hasOverride));
        await syncButton(button);
        button.disabled = false;
        setResult(button, "success", "Environment override removed. Reloading editor…");
        window.setTimeout(function() {
          window.location.reload();
        }, 180);
      } catch (error) {
        button.disabled = false;
        setResult(button, "danger", (error && error.description) || "Unable to reset this setting right now.");
        if (window.console && typeof console.error === "function") {
          console.error("Config reset failed.", error);
        }
      }
    });
  });
};
